/**
 * The route test's one gateway choice, in a client's words (user,
 * 19/10/2026): drops that have no gateway chosen on them either take turns
 * through the gateways or all go through the first one — with a live line
 * saying what that does to today's drops. Saved as the strategy ALTERNATE /
 * HOLD_UNTIL_FAIL (manual order, no weights), as before.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Shuffle } from 'lucide-react';

import { HintDot } from '@/components/ui';

import { ChoiceChips } from '../../general/controls/ChoiceChips';
import { type TestSpreadChoice, type TestSpreadFacts, testSpreadChoiceOf, testSpreadLine } from './test-spread';

const OPTIONS = [
  { value: 'ALTERNATE', label: 'Take turns' },
  { value: 'HOLD_UNTIL_FAIL', label: 'All through A' },
] as const;

const HINT =
  'A drop can have its own gateway (General & Slots › Drop slots › Gateway). This choice is for every other drop. Take turns: A, B, C, then A again, so each gateway is tested. All through A: they all use the first gateway in the list. If a gateway is blocked by its verdict or full for the day (SMS a day), the next one takes its turn.';

interface Props extends TestSpreadFacts {
  strategy: string;
  onChange: (strategy: TestSpreadChoice) => void;
}

export const TestSpreadRow: FC<Props> = ({ strategy, slots, gatewayCount, onChange }) => {
  const choice = testSpreadChoiceOf(strategy);
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-border/60 bg-background px-3 py-2" data-testid="test-spread">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
          <Shuffle className="h-3 w-3" aria-hidden /> Drops without a gateway <HintDot label={HINT} />
        </span>
        <ChoiceChips<TestSpreadChoice> options={OPTIONS} value={choice} onChange={onChange} ariaLabel="Where drops without a gateway go" />
      </div>
      <p key={choice} className="text-[11px] leading-snug text-muted-foreground duration-300 animate-in fade-in-0 motion-reduce:animate-none">
        {testSpreadLine(choice, { slots, gatewayCount })}
      </p>
    </div>
  );
};
